import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
// Using RichTextEditor for formatted comments instead of Textarea
import RichTextEditor from '../components/RichTextEditor';
import { toast } from 'sonner';
import { ArrowLeft, MessageSquare, Paperclip, Send, Loader2, Download, Activity, Plus, UserCheck, AlertTriangle, MessageCircle, FileText, Edit2, Save, X, Edit, Bot } from 'lucide-react';
import { format } from 'date-fns';

const TicketDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isInternalComment, setIsInternalComment] = useState(true); // Default to true, will be updated based on ticket creator
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showPublicCommentConfirm, setShowPublicCommentConfirm] = useState(false);
  const [pendingComment, setPendingComment] = useState(null);
  
  // RCA states
  const [showRcaDialog, setShowRcaDialog] = useState(false);
  const [rcaText, setRcaText] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [submittingRca, setSubmittingRca] = useState(false);

  // Reopen states
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopenText, setReopenText] = useState('');
  const [submittingReopen, setSubmittingReopen] = useState(false);
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    product_id: '',
    category_id: '',
    subcategory_id: '',
    issue_type_id: '',
    priority_id: ''
  });
  
  // Dropdown data for editing
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchTicketDetails();
    fetchUsers();
    fetchComments();
    fetchAttachments();
    fetchActivities();
    fetchDropdownData();
    
    // Listen for ticket updates via WebSocket notifications
    const handleTicketUpdate = (event) => {
      if (event.detail.ticketId === parseInt(id)) {
        console.log('Ticket updated, refreshing details...');
        fetchTicketDetails();
        fetchComments();
        fetchAttachments();
        fetchActivities();
        toast.info('Ticket updated', { duration: 2000 });
      }
    };
    
    window.addEventListener('ticket-updated', handleTicketUpdate);
    
    return () => {
      window.removeEventListener('ticket-updated', handleTicketUpdate);
    };
  }, [id]);

  // Set internal comment default based on ticket creator's role
  useEffect(() => {
    if (ticket && ticket.creator) {
      // If ticket was created by a client, default to false (public comment)
      // Otherwise, default to true (internal comment)
      setIsInternalComment(ticket.creator.role === 'Client');
    }
  }, [ticket]);

  const fetchDropdownData = async () => {
    try {
      const [productsRes, issueTypesRes, prioritiesRes] = await Promise.all([
        api.get('/products'),
        api.get('/issue-types'),
        api.get('/priorities')
      ]);
      
      setProducts(productsRes.data);
      setIssueTypes(issueTypesRes.data);
      setPriorities(prioritiesRes.data);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchCategories = async (productId) => {
    try {
      const response = await api.get(`/categories?product_id=${productId}`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const response = await api.get(`/subcategories?category_id=${categoryId}`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      setSubcategories([]);
    }
  };

  const fetchTicketDetails = async () => {
    try {
      const response = await api.get(`/tickets/${id}`);
      setTicket(response.data);
    } catch (error) {
      toast.error('Failed to fetch ticket details');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await api.get(`/tickets/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await api.get(`/tickets/${id}/attachments`);
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      setActivitiesLoading(true);
      const response = await api.get(`/tickets/${id}/activities`);
      setActivityLog(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const formatActivityMessage = (activity) => {
    const renderValue = (val) => {
      if (!val && val !== 0) return null;
      const str = String(val);
      const looksLikeHTML = /<[^>]+>/.test(str);
      if (looksLikeHTML) {
        return <RichTextEditor value={str} readOnly className="prose max-w-none text-sm text-slate-700" />;
      }
      return <span>{val}</span>;
    };

    if (activity.action === 'status_changed' && 
        (activity.new_value === 'Resolved' || activity.new_value === 'RESOLVED') && 
        activity.rca_text) {
      return (
        <div className="space-y-1">
          <div>
            <strong>Status changed</strong>: {activity.old_value} → <strong>Resolved</strong>
          </div>
          <div className="mt-1.5 p-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs">
            <span className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">Root Cause Analysis (RCA):</span>
            <span className="text-slate-600 dark:text-slate-400 italic whitespace-pre-wrap">{activity.rca_text}</span>
          </div>
        </div>
      );
    }

    if (activity.action === 'status_changed' && 
        (activity.new_value === 'OPEN' || activity.new_value === 'IN_PROGRESS' || activity.new_value === 'Open' || activity.new_value === 'In Progress') && 
        activity.reopen_text) {
      return (
        <div className="space-y-1">
          <div>
            <strong>Status changed</strong>: {activity.old_value} → <strong>{activity.new_value}</strong>
          </div>
          <div className="mt-1.5 p-3 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 text-xs">
            <span className="font-semibold text-orange-800 dark:text-orange-300 block mb-1">Reopen Explanation:</span>
            <span className="text-slate-600 dark:text-slate-400 italic whitespace-pre-wrap">{activity.reopen_text}</span>
          </div>
        </div>
      );
    }

    const actionMessages = {
      'created': 'Ticket created',
      'assigned': 'Ticket assigned',
      'reassigned': 'Ticket reassigned',
      'status_changed': 'Status changed',
      'priority_changed': 'Priority changed',
      'title_changed': 'Title changed',
      'description_changed': 'Description changed',
      'product_changed': 'Product changed',
      'category_changed': 'Category changed',
      'subcategory_changed': 'Subcategory changed',
      'issue_type_changed': 'Issue type changed',
      'comment_added': 'Comment added',
      'attachment_added': 'Attachment added'
    };

    const message = actionMessages[activity.action] || activity.action.replace('_', ' ');
    
    if (activity.old_value && activity.new_value) {
      return (
        <>
          <strong>{message}</strong>: {renderValue(activity.old_value)} → {renderValue(activity.new_value)}
        </>
      );
    } else if (activity.new_value) {
      return (
        <>
          <strong>{message}</strong>: {renderValue(activity.new_value)}
        </>
      );
    }
    
    return message;
  };

  const getActivityIcon = (action) => {
    const iconMap = {
      'created': Plus,
      'assigned': UserCheck,
      'reassigned': UserCheck,
      'status_changed': AlertTriangle,
      'priority_changed': AlertTriangle,
      'title_changed': Edit,
      'description_changed': Edit,
      'product_changed': Edit,
      'category_changed': Edit,
      'subcategory_changed': Edit,
      'issue_type_changed': Edit,
      'comment_added': MessageCircle,
      'attachment_added': FileText
    };
    
    return iconMap[action] || Activity;
  };

  const handleStatusChange = async (newStatus) => {
    if (newStatus === 'Resolved') {
      setPendingStatus('Resolved');
      setRcaText('');
      setShowRcaDialog(true);
      return;
    }

    if (newStatus === 'Closed') {
      if (!ticket.rca_text) {
        setPendingStatus('Closed');
        setRcaText('');
        setShowRcaDialog(true);
        return;
      }
    }

    const isReopening = (ticket.status === 'Closed' || ticket.status === 'Resolved') && 
                        (newStatus === 'Open' || newStatus === 'In Progress');
                        
    if (isReopening) {
      setPendingStatus(newStatus);
      setReopenText('');
      setShowReopenDialog(true);
      return;
    }
    
    try {
      await api.put(`/tickets/${id}`, { status: newStatus });
      setTicket({ ...ticket, status: newStatus });
      // Fetch updated activities from API
      fetchActivities();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleReopenSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!reopenText.trim()) {
      toast.error('Reopen explanation is required');
      return;
    }
    setSubmittingReopen(true);
    try {
      await api.put(`/tickets/${id}`, { status: pendingStatus, reopen_text: reopenText });
      setTicket({ ...ticket, status: pendingStatus });
      setShowReopenDialog(false);
      setReopenText('');
      setPendingStatus(null);
      // Fetch updated activities and comments from API
      fetchActivities();
      fetchComments();
      toast.success('Ticket reopened successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen ticket');
    } finally {
      setSubmittingReopen(false);
    }
  };

  const handleRcaSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!rcaText.trim()) {
      toast.error('Root Cause Analysis (RCA) is required');
      return;
    }
    setSubmittingRca(true);
    try {
      await api.put(`/tickets/${id}`, { status: pendingStatus, rca_text: rcaText });
      setTicket({ 
        ...ticket, 
        status: pendingStatus, 
        rca_text: rcaText,
        rca_created_by: user.full_name || user.username,
        rca_created_at: new Date().toISOString()
      });
      setShowRcaDialog(false);
      setRcaText('');
      // Fetch updated activities from API
      fetchActivities();
      toast.success(`Ticket ${pendingStatus === 'Closed' ? 'closed' : 'resolved'} successfully with RCA`);
      setPendingStatus(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${pendingStatus === 'Closed' ? 'close' : 'resolve'} ticket`);
    } finally {
      setSubmittingRca(false);
    }
  };

  const handleAssigneeChange = async (assigneeId) => {
    try {
      await api.put(`/tickets/${id}`, { assigned_to_id: parseInt(assigneeId) });
      const assignee = users.find(u => u.id === parseInt(assigneeId));
      setTicket({ ...ticket, assigned_to_id: parseInt(assigneeId), assignee });
      // Fetch updated activities from API
      fetchActivities();
      toast.success('Assignee updated successfully');
    } catch (error) {
      toast.error('Failed to update assignee');
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/attachments/${attachmentId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    // Avoid submitting empty HTML (e.g. <p><br></p>) by stripping tags
    const plainText = newComment ? newComment.replace(/<[^>]+>/g, '').trim() : '';
    if (!plainText) {
      toast.error('Comment cannot be empty');
      return;
    }

    // Check if this is a client-created ticket and the comment is not marked as internal
    if (ticket?.creator?.role === 'Client' && !isInternalComment) {
      // Show confirmation dialog
      setPendingComment({ comment: newComment, is_internal: isInternalComment });
      setShowPublicCommentConfirm(true);
      return;
    }

    // If internal or not a client ticket, submit directly
    await submitComment(newComment, isInternalComment);
  };

  const submitComment = async (commentText, isInternal) => {
    setSubmitting(true);
    try {
      const response = await api.post(`/tickets/${id}/comments`, { 
        comment: commentText,
        is_internal: isInternal 
      });
      setComments([...comments, response.data]);
      setNewComment('');
      // Reset to default based on ticket creator's role
      setIsInternalComment(true);
      // Fetch updated activities from API
      fetchActivities();
      toast.success('Comment added successfully');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPublicComment = async () => {
    setShowPublicCommentConfirm(false);
    if (pendingComment) {
      await submitComment(pendingComment.comment, pendingComment.is_internal);
      setPendingComment(null);
    }
  };

  const handlePostAsInternal = async () => {
    setShowPublicCommentConfirm(false);
    if (pendingComment) {
      await submitComment(pendingComment.comment, true); // Force as internal
      setPendingComment(null);
    }
  };

  const handleCancelPublicComment = () => {
    setShowPublicCommentConfirm(false);
    setPendingComment(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/tickets/${id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAttachments([...attachments, response.data]);
      // Fetch updated activities from API
      fetchActivities();
      toast.success('File uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      e.target.value = '';
    }
  };

  const handleEditStart = () => {
    setEditForm({
      title: ticket.title || '',
      // ensure description is a string (Quill controlled value expects string)
      description: ticket.description || '',
      // use empty string for unset ids to prevent .toString() errors in Select value
      product_id: ticket.product_id ?? '',
      category_id: ticket.category_id ?? '',
      subcategory_id: ticket.subcategory_id ?? '',
      issue_type_id: ticket.issue_type_id ?? '',
      priority_id: ticket.priority_id ?? ''
    });
    
    // Fetch categories based on the current product
    if (ticket.product_id) {
      fetchCategories(ticket.product_id);
    }
    
    // Fetch subcategories if category is selected
    if (ticket.category_id) {
      fetchSubcategories(ticket.category_id);
    }
    
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditForm({
      title: '',
      description: '',
      product_id: '',
      category_id: '',
      subcategory_id: '',
      issue_type_id: '',
      priority_id: ''
    });
    // Clear dependent dropdowns
    setCategories([]);
    setSubcategories([]);
  };

  const handleEditSave = async () => {
    if (!editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setEditLoading(true);
    try {
      const updateData = {};
      
      // Only include fields that have changed
      if (editForm.title !== ticket.title) updateData.title = editForm.title;
      if (editForm.description !== ticket.description) updateData.description = editForm.description;
      if (editForm.product_id !== ticket.product_id) updateData.product_id = parseInt(editForm.product_id);
      if (editForm.category_id !== ticket.category_id) updateData.category_id = parseInt(editForm.category_id);
      if (editForm.subcategory_id !== ticket.subcategory_id) {
        updateData.subcategory_id = editForm.subcategory_id ? parseInt(editForm.subcategory_id) : null;
      }
      if (editForm.issue_type_id !== ticket.issue_type_id) updateData.issue_type_id = parseInt(editForm.issue_type_id);
      if (editForm.priority_id !== ticket.priority_id) updateData.priority_id = parseInt(editForm.priority_id);

      if (Object.keys(updateData).length === 0) {
        toast.info('No changes to save');
        setIsEditing(false);
        return;
      }

      await api.put(`/tickets/${id}`, updateData);
      
      // Refresh ticket data and activities
      await fetchTicketDetails();
      await fetchActivities();
      
      setIsEditing(false);
      toast.success('Ticket updated successfully');
    } catch (error) {
      toast.error('Failed to update ticket');
    } finally {
      setEditLoading(false);
    }
  };

  const handleProductChange = (productId) => {
    setEditForm({ 
      ...editForm, 
      product_id: parseInt(productId), 
      category_id: '', 
      subcategory_id: '' 
    });
    setCategories([]);
    setSubcategories([]);
    if (productId) {
      fetchCategories(productId);
    }
  };

  const handleCategoryChange = (categoryId) => {
    setEditForm({ ...editForm, category_id: parseInt(categoryId), subcategory_id: null });
    setSubcategories([]);
    if (categoryId) {
      fetchSubcategories(categoryId);
    }
  };

  const statusColors = {
    'Open': 'bg-amber-50 text-amber-700 border-amber-200',
    'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
    'Resolved': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    'Closed': 'bg-slate-100 text-slate-600 border-slate-200',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center p-12">Ticket not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white mb-2">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-3 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn" className="gap-2 -ml-3 px-2">
                      <ArrowLeft size={18} />
                      Back
                    </Button>
                    <span className="text-sm font-mono text-indigo-600 font-bold" data-testid="ticket-id">
                      {ticket.ticket_id}
                    </span>
                    <Badge className={statusColors[ticket.status] + ' border'} data-testid="ticket-status">
                      {ticket.status}
                    </Badge>
                    {ticket.priority && (
                      <Badge
                        style={{ backgroundColor: ticket.priority.color + '15', color: ticket.priority.color, borderColor: ticket.priority.color + '40' }}
                        className="border font-medium"
                        data-testid="ticket-priority"
                      >
                        {ticket.priority.name}
                      </Badge>
                    )}
                  </div>
                  
                  {isEditing ? (
                    <div className="space-y-3">
                      <Input
                        value={editForm.title}
                        onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                        className="text-2xl font-bold border-slate-300"
                        placeholder="Ticket title"
                      />
                    </div>
                  ) : (
                    <CardTitle className="text-3xl font-bold" data-testid="ticket-title">{ticket.title}</CardTitle>
                  )}
                </div>
                
                {/* Edit Button - Only show if not closed and user has permission */}
                {ticket.status !== 'Closed' && !isEditing && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditStart}
                    className="ml-4 border-slate-300 hover:bg-slate-50"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                
                {/* Save/Cancel Buttons - Only show when editing */}
                {isEditing && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleEditCancel}
                      disabled={editLoading}
                      className="border-slate-300 hover:bg-slate-50"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleEditSave}
                      disabled={editLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {editLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Save
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-sm font-semibold text-slate-700">Description</Label>
                <div className="mt-2">
                  <RichTextEditor
                    readOnly={isEditing? false : true}
                    value={isEditing ? editForm.description : ticket.description}
                    // Only attach onChange when editing to avoid updates while in read-only mode
                    onChange={isEditing ? (value) => setEditForm(prev => ({ ...prev, description: value ?? '' })) : undefined}
                    placeholder="Enter ticket description..."
                    className={isEditing? '' : "prose prose-sm max-w-none"}
                    data-testid="ticket-description"
                  />
                </div>
              </div>

              <Separator />

              {isEditing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Product</Label>
                    <Select value={editForm.product_id.toString()} onValueChange={handleProductChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id.toString()}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select 
                      value={editForm.category_id ? editForm.category_id.toString() : ''} 
                      onValueChange={handleCategoryChange}
                      disabled={!editForm.product_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={editForm.product_id ? "Select category" : "Select product first"} />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id.toString()}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {editForm.category_id && (
                    <div className="space-y-2">
                      <Label>Subcategory</Label>
                      <Select 
                        value={editForm.subcategory_id?.toString() || 'none'} 
                        onValueChange={(value) => setEditForm({ ...editForm, subcategory_id: value === 'none' ? null : parseInt(value) })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select subcategory" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {subcategories.map((subcategory) => (
                            <SelectItem key={subcategory.id} value={subcategory.id.toString()}>
                              {subcategory.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Issue Type</Label>
                    <Select value={editForm.issue_type_id.toString()} onValueChange={(value) => setEditForm({ ...editForm, issue_type_id: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select issue type" />
                      </SelectTrigger>
                      <SelectContent>
                        {issueTypes.map((issueType) => (
                          <SelectItem key={issueType.id} value={issueType.id.toString()}>
                            {issueType.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select value={editForm.priority_id.toString()} onValueChange={(value) => setEditForm({ ...editForm, priority_id: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {priorities.map((priority) => (
                          <SelectItem key={priority.id} value={priority.id.toString()}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: priority.color }}
                              />
                              {priority.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-600">Product</Label>
                    <p className="font-medium mt-1">{ticket.product?.name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Category</Label>
                    <p className="font-medium mt-1">{ticket.category?.name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Issue Type</Label>
                    <p className="font-medium mt-1">{ticket.issue_type?.name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Created By</Label>
                    <p className="font-medium mt-1">
                      {ticket.creator?.full_name || ticket.creator?.username}
                      {ticket.creator?.role === 'Client' && (
                        <span className="ml-1 text-indigo-600 font-semibold">(Client)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Created At</Label>
                    <p className="font-medium mt-1">{format(new Date(ticket.created_at), 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Updated At</Label>
                    <p className="font-medium mt-1">{format(new Date(ticket.updated_at), 'MMM dd, yyyy HH:mm')}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white mb-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
                <MessageSquare size={20} />
                Comments ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-4 border border-slate-200 dark:border-slate-700 dark:bg-slate-800/50 rounded-xl bg-white" data-testid={`comment-${comment.id}`}>
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-blue-500 text-white text-sm">
                      {comment.user?.full_name?.charAt(0) || comment.user?.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">
                        {comment.user?.full_name || comment.user?.username}
                        {comment.user?.role === 'Client' && (
                          <span className="ml-1 text-indigo-600 font-semibold">(Client)</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500">{format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}</span>
                      {comment.is_internal && (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800 text-xs">
                          Internal
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-slate-700">
                      <RichTextEditor value={comment.comment} readOnly className="prose max-w-none" />
                    </div>
                  </div>
                </div>
              ))}

              <form onSubmit={handleCommentSubmit} className="space-y-3" data-testid="comment-form">
                <div data-testid="comment-input">
                  <RichTextEditor
                    value={newComment}
                    onChange={(value) => setNewComment(value)}
                    placeholder="Add a comment..."
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isInternalComment}
                      onChange={(e) => setIsInternalComment(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span>Internal Comment (visible only to staff)</span>
                  </label>
                  <Button type="submit" data-testid="submit-comment-btn" disabled={submitting} className="bg-blue-500 hover:bg-blue-600 text-white">
                    {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                    Add Comment
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Attachments Section */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white mb-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
                <Paperclip size={20} />
                Attachments ({attachments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-700 dark:bg-slate-800/50 rounded-xl hover:border-indigo-200 dark:hover:border-indigo-400 transition-all bg-white" data-testid={`attachment-${attachment.id}`}>
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-slate-900 dark:text-white">{attachment.filename}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {(attachment.file_size / 1024).toFixed(2)} KB • {format(new Date(attachment.created_at), 'MMM dd, yyyy HH:mm')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadAttachment(attachment.id, attachment.filename)}
                    className="ml-3 border-slate-300 hover:bg-indigo-50 hover:border-indigo-300"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              ))}

              <div className="space-y-2">
                <input
                  type="file"
                  id="file-upload"
                  data-testid="file-upload-input"
                  className="hidden"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                  accept=".pdf,.doc,.docx,.xlsx,.xls,.ppt,.pptx,.txt,.zip,.png,.jpg,.jpeg,.gif,.webp"
                />
                <label htmlFor="file-upload">
                  <Button type="button" asChild disabled={uploadingFile} variant="outline" className="cursor-pointer border-slate-300 hover:bg-indigo-50">
                    <span>
                      {uploadingFile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />}
                      Upload File
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-slate-500">
                  Allowed: PDF, DOC, DOCX, XLSX, PPT, TXT, ZIP, Images (Max 10MB)
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Activity Tracker */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white mb-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg dark:text-white">
                <Activity size={20} />
                Activity Log
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activitiesLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                </div>
              ) : activityLog.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No activity recorded yet</p>
              ) : (
                <div className="space-y-3">
                  {activityLog.map((activity) => {
                    const IconComponent = getActivityIcon(activity.action);
                    return (
                      <div key={activity.id} className="flex gap-3 p-3 border border-slate-200 dark:border-slate-700 dark:bg-slate-800/50 rounded-lg bg-slate-50 dark:bg-slate-800">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                          <IconComponent size={16} className="text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-slate-900 dark:text-white">{formatActivityMessage(activity)}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            {activity.performed_by ? (activity.performed_by) : 'System'} • {format(new Date(activity.created_at), 'MMM dd, yyyy HH:mm')}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-900/80 shadow-sm bg-white mb-2">
            <CardHeader>
              <CardTitle className="text-lg dark:text-white">Ticket Management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Status</Label>
                {ticket.status === 'Closed' ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-slate-600 hover:bg-slate-600 text-white font-semibold py-1 px-3 text-sm rounded-md">
                      Closed
                    </Badge>
                    <Button
                      onClick={() => handleStatusChange('Open')}
                      size="sm"
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold flex items-center gap-1.5 h-8 px-3 rounded-md text-xs"
                    >
                      <Bot size={12} />
                      Reopen Ticket
                    </Button>
                  </div>
                ) : (
                  <Select value={ticket.status} onValueChange={handleStatusChange}>
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Resolved">Resolved</SelectItem>
                      <SelectItem value="Closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Assigned To</Label>
                <Select
                  value={ticket.assigned_to_id?.toString() || 'unassigned'}
                  onValueChange={handleAssigneeChange}
                  disabled={ticket.status === 'Closed'}
                >
                    <SelectTrigger data-testid="assignee-select">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.filter(u => u.role !== 'Client' && u.is_active).map((u) => (
                      <SelectItem key={u.id} value={u.id.toString()}>
                        {u.full_name || u.username}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {ticket.rca_text && (
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2" data-testid="ticket-rca-section">
                  <Label className="text-xs font-semibold text-slate-500 dark:text-slate-400">Root Cause Analysis (RCA)</Label>
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200 dark:border-amber-900/30 text-base whitespace-pre-wrap text-amber-900 dark:text-amber-200 italic font-medium">
                    {ticket.rca_text}
                    {(ticket.rca_created_by || ticket.rca_created_at) && (
                      <div className="mt-2 pt-2 border-t border-amber-200/50 dark:border-amber-900/20 text-xxs font-normal not-italic text-amber-700/80 dark:text-amber-400/80 flex justify-between items-center">
                        <span>By: {ticket.rca_created_by || 'System'}</span>
                        <span>{ticket.rca_created_at ? format(new Date(ticket.rca_created_at), 'MMM dd, yyyy HH:mm') : ''}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Public Comment Confirmation Dialog */}
      <AlertDialog open={showPublicCommentConfirm} onOpenChange={setShowPublicCommentConfirm}>
        <AlertDialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800 max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Comment
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p className="font-semibold text-slate-900">
                This ticket was created by a client and your comment will be visible to them.
              </p>
              <p>
                Are you sure you want to post this as a <strong>public comment</strong>? 
              </p>
              <p className="text-sm text-slate-600">
                If this comment contains sensitive or internal information, you can post it as an internal comment instead.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={handleCancelPublicComment} className="mt-0">
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handlePostAsInternal}
              variant="outline"
              className="border-amber-600 text-amber-700 hover:bg-amber-50"
            >
              Post as Internal Comment
            </Button>
            <AlertDialogAction 
              onClick={handleConfirmPublicComment}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Yes, Post Public Comment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Root Cause Analysis (RCA) Dialog */}
      <Dialog open={showRcaDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRcaDialog(false);
          setPendingStatus(null);
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
              <Bot className="h-5 w-5" />
              Root Cause Analysis (RCA)
            </DialogTitle>
            <DialogDescription>
              Please describe the root cause of this issue. This is required for {pendingStatus === 'Closed' ? 'closing' : 'resolving'} the ticket.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRcaSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="rca-text" className="text-sm font-semibold">Root Cause Explanation *</Label>
              <textarea
                id="rca-text"
                value={rcaText}
                onChange={(e) => setRcaText(e.target.value)}
                placeholder="Describe why this issue occurred and how it was resolved..."
                rows={4}
                className="w-full min-h-[100px] p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRcaDialog(false);
                  setPendingStatus(null);
                }}
                disabled={submittingRca}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingRca || !rcaText.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              >
                {submittingRca ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  pendingStatus === 'Closed' ? 'Close Ticket' : 'Resolve Ticket'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reopen Ticket Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={(open) => {
        if (!open) {
          setShowReopenDialog(false);
          setPendingStatus(null);
        }
      }}>
        <DialogContent className="bg-white dark:bg-slate-900 dark:border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <Bot className="h-5 w-5" />
              Reopen Ticket
            </DialogTitle>
            <DialogDescription>
              Please describe why this ticket is being reopened. A comment will be posted with this description.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleReopenSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="reopen-text" className="text-sm font-semibold">Reopen Explanation *</Label>
              <textarea
                id="reopen-text"
                value={reopenText}
                onChange={(e) => setReopenText(e.target.value)}
                placeholder="Describe why this ticket needs to be reopened..."
                rows={4}
                className="w-full min-h-[100px] p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowReopenDialog(false);
                  setPendingStatus(null);
                }}
                disabled={submittingReopen}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submittingReopen || !reopenText.trim()}
                className="bg-orange-600 hover:bg-orange-700 text-white font-semibold"
              >
                {submittingReopen ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Reopening...
                  </>
                ) : (
                  'Reopen Ticket'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;